<script setup lang="ts">
/**
 * What a member did during a turn, under the reply it produced. A run it started stays on one visible
 * line — `<task name> · <result>`, and where to open it — because that is the part a CEO acts on (#1761).
 * Two or more runs in the turn fold into one closed line that counts their results, and opens to each
 * run's own line. Everything else the calls said and returned is a log: closed by default, and rendered
 * by the same payload viewer the run pages use, so an object reads as an object here too.
 *
 * It sits inside a ZhChatThread bubble, so it speaks the bubble's v2 language (#972): the run's status, the
 * log's summary, each call's tool name and "Open run" (at the system tone, the cyan the thread's own run card
 * opens with) are all the shared system label (ZhSystemLabel, BG §19.3), and the rule above it a hairline.
 * Type comes from the bubble and the kit (#1229): nothing here declares a size, face, weight or tracking.
 */
import { ZhJson, ZhSystemLabel } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import ExecutionResources from '@/components/ExecutionResources.vue'
import { type ChatToolItem, TASK_UNKNOWN } from '@/lib/chat'

const props = defineProps<{ items: ChatToolItem[] }>()

const runs = computed(() => props.items.filter(item => item.runId !== null))
const summary = computed(() => `Log · ${props.items.length} tool ${props.items.length === 1 ? 'call' : 'calls'}`)

/** Two or more runs started in this turn fold into one closed line (#1761); one run is its own line. */
const grouped = computed(() => runs.value.length > 1)

/** The folded line: how many runs, then how many of each result, in the order each result first appears. */
const runSummary = computed(() => {
    const counts = new Map<string, number>()
    for (const item of runs.value) {
        const result = item.result || item.status
        if (result) {
            counts.set(result, (counts.get(result) ?? 0) + 1)
        }
    }
    return [`${runs.value.length} runs`, ...[...counts].map(([result, count]) => `${count} ${result}`)].join(' · ')
})
</script>

<template>
	<div data-testid="chat-tool-log" class="chat-tool-log">
		<component :is="grouped ? 'details' : 'div'" v-if="runs.length > 0">
			<ZhSystemLabel v-if="grouped" as="summary" tone="muted" class="chat-tool-log__summary">{{ runSummary }}</ZhSystemLabel>
			<ol :class="{ 'chat-tool-log__calls': grouped }">
				<li v-for="item in runs" :key="item.id" class="chat-tool-log__run" data-testid="chat-tool-run">
					<p class="chat-tool-log__line">
						<span class="chat-tool-log__started">{{ item.taskLabel || TASK_UNKNOWN }}{{ item.status ? ' · ' : '' }}</span>
						<ZhSystemLabel v-if="item.status" as="span" tone="muted">{{ item.status }}</ZhSystemLabel>
						<RouterLink class="chat-tool-log__open" :to="`/runs/${item.runId}`">
							<ZhSystemLabel as="span" tone="system">Open run</ZhSystemLabel>
						</RouterLink>
					</p>
					<div v-if="item.resources.length" class="chat-tool-log__built">
						<ZhSystemLabel>What this built</ZhSystemLabel>
						<ExecutionResources :resources="item.resources" />
					</div>
				</li>
			</ol>
		</component>
		<details>
			<ZhSystemLabel as="summary" tone="muted" class="chat-tool-log__summary">{{ summary }}</ZhSystemLabel>
			<ol class="chat-tool-log__calls">
				<li v-for="item in items" :key="item.id" class="chat-tool-log__call" data-testid="chat-tool-call">
					<ZhSystemLabel class="chat-tool-log__tool">{{ item.tool }}</ZhSystemLabel>
					<ZhJson v-if="item.payload !== null" :value="item.payload" label="Tool payload" />
				</li>
			</ol>
		</details>
	</div>
</template>

<style scoped>
p,
ol {
	margin: 0;
}

ol {
	padding: 0;
	list-style: none;
}

/* Under the reply, set off by the hairline the thread's own head uses (ZhChatThread `.zh-chat__head`). */
.chat-tool-log {
	display: grid;
	gap: 0.5rem;
	margin-top: 0.75rem;
	padding-top: 0.75rem;
	border-top: 1px solid var(--hair);
	color: var(--ink-2);
}

/* A turn where the member only worked: the log is the whole bubble, so it needs no rule above it. */
.chat-tool-log:first-child {
	margin-top: 0;
	padding-top: 0;
	border-top: 0;
}

.chat-tool-log__run {
	display: grid;
	gap: 0.375rem;
}

.chat-tool-log__line {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	column-gap: 0.75rem;
	row-gap: 0.25rem;
}

/* The task the run is for reads a step brighter than the rest of the log, on the kit's ink scale. */
.chat-tool-log__started {
	color: var(--ink-1);
}

/* Where to open it: the system label at its system tone draws it; the link only adds the underline on hover. */
.chat-tool-log__open {
	text-decoration: none;
}

.chat-tool-log__open:hover,
.chat-tool-log__open:focus-visible {
	text-decoration: underline;
	text-underline-offset: 0.2em;
}

.chat-tool-log__built {
	display: grid;
	gap: 0.375rem;
	padding-top: 0.5rem;
	border-top: 1px solid var(--hair);
}

.chat-tool-log__summary {
	cursor: pointer;
	user-select: none;
}

.chat-tool-log__calls {
	display: grid;
	gap: 0.5rem;
	margin-top: 0.5rem;
}

.chat-tool-log__call {
	display: grid;
	gap: 0.25rem;
}

/* The tool name is a system label; a long dotted name may still break rather than widen the bubble. */
.chat-tool-log__tool {
	overflow-wrap: anywhere;
}
</style>
